"use no memo";

import type { CellContext, ColumnDef } from "@tanstack/react-table";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import DesoAvatar from "@/components/shared/deso-avatar";
import { MaybeUserLabel } from "@/components/shared/maybe-user-label";
import { TimestampTooltip } from "@/components/shared/timestamp-tooltip";
import TransactionCopyToClipboard from "@/components/shared/transaction-copy-to-clipboard";
import {
  TransactionVolumeBadge,
  TransactionVolumeBadgeType,
} from "@/components/shared/transaction-volume-badge";
import { UserDisplayNameWithStatus } from "@/components/shared/user-display-name-with-status";
import { Badge } from "@/components/ui/badge";
import type { TradingRecentTrade } from "@/graphql/codegen/graphql";
import { FOCUS_TOKEN_DISPLAY_NAME } from "@/utils/constants";
import { usdToFocus } from "@/utils/currency";
import getContentBlurredStyle from "@/utils/get-content-blurred-style";
import { isDesoFoundationToken, isWrappedAsset } from "@/utils/helpers";
import { cn } from "@/utils/lib";

import TradeCoinActions from "../actions/trade-coin-actions";
import { renderSortingHeader } from "../table-sorting-headers";

interface CustomCellContext extends CellContext<TradingRecentTrade, unknown> {
  focusExchangeRate: number;
  pageOffset: number;
  currentUserPublicKey: string;
  refetchData: () => void;
}

export enum TradeType {
  Buy = "BUY",
  Sell = "SELL",
}

const renderTransactionVolumeBadgeBasedOnValue = (valueUsd: number) => {
  const badgeConfigs = [
    { type: TransactionVolumeBadgeType.Whale, min: 10000, width: 22 },
    { type: TransactionVolumeBadgeType.Dolphin, min: 1000, width: 18 },
    { type: TransactionVolumeBadgeType.Fish, min: 100, width: 18 },
    { type: TransactionVolumeBadgeType.Shrimp, min: 0, width: 18 },
  ];

  const badgeConfig = badgeConfigs.find((config) => valueUsd >= config.min);

  if (!badgeConfig) {
    return null;
  }

  return (
    <TransactionVolumeBadge
      type={badgeConfig.type}
      text={
        <CoinPriceFormatter
          price={valueUsd}
          abbreviatePriceThreshold={Number.MAX_SAFE_INTEGER}
          highPriceThreshold={1}
        />
      }
      width={badgeConfig.width}
    />
  );
};

const PublicKeyStub = () => (
  <div className="flex items-center gap-2">
    <DesoAvatar account={null} size={32} />{" "}
    <span className="truncate font-sans text-sm text-muted underline-offset-4">
      Unknown
    </span>
  </div>
);

export const columns = (): ColumnDef<TradingRecentTrade>[] => {
  return [
    {
      accessorKey: "tradeTimestamp",
      header: () => <span className="text-xxs">Time</span>,
      cell: ({ row }) => (
        <TimestampTooltip
          timestamp={row.original.tradeTimestamp}
          className={cn(
            "cursor-pointer",
            row.original.tradeType === TradeType.Buy
              ? "text-green-500"
              : "text-red-500",
          )}
        />
      ),
    },
    {
      accessorKey: "tradeType",
      header: () => <span className="text-xxs">Type</span>,
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.tradeType === TradeType.Buy ? "success" : "destructive"
          }
          className="text-xs"
        >
          {row.original.tradeType}
        </Badge>
      ),
    },
    {
      accessorKey: "traderPublicKey",
      header: () => <span className="text-xxs">Trader</span>,
      cell: ({ row }) => {
        if (!row.original.traderPublicKey) {
          return <PublicKeyStub />;
        }
        return (
          <MaybeUserLabel
            value={row.original.traderPublicKey}
            showDisplayName={true}
            shortenIfNoProfile={true}
            showAvatarIfNoProfile={true}
            clickable={true}
            imageSize={32}
            className="items-center"
            displayNameWrapperClassName="w-[130px]"
            displayNameClassName="pl-0 w-[130px] text-sm text-left grow overflow-hidden underline-offset-4 truncate font-sans"
            style={getContentBlurredStyle(row.original.traderPublicKey?.length)}
          />
        );
      },
    },
    {
      accessorKey: "tokenPublicKey",
      header: () => <span className="text-xxs">Token</span>,
      cell: ({ row }) => {
        const wrappedAsset =
          row.original.tokenPublicKey &&
          (isWrappedAsset(row.original.tokenPublicKey) ||
            isDesoFoundationToken(row.original.tokenPublicKey));
        const blurredStyleWidth =
          row.original.tokenUsername?.length ||
          row.original.tokenPublicKey?.length;

        if (!row.original.tokenPublicKey) {
          return <PublicKeyStub />;
        }

        return (
          <div className="flex items-center gap-2">
            <DesoAvatar
              account={
                row.original.tokenPublicKey
                  ? {
                      // Note: no ability to pass extra data to the DesoAvatar component
                      publicKey: row.original.tokenPublicKey,
                      username: row.original.tokenUsername,
                    }
                  : null
              }
              imgURL={row.original.tokenProfilePicUrl ?? ""}
              size={24}
            />

            <div
              className="flex flex-col"
              style={getContentBlurredStyle(
                wrappedAsset ? 20 : blurredStyleWidth,
              )}
            >
              <UserDisplayNameWithStatus
                whaleSize={16}
                clickable={true}
                {...{
                  username: row.original.tokenUsername ?? "",
                  publicKey: row.original.tokenPublicKey ?? "",
                  className:
                    "pl-0 w-[75px] text-left grow font-normal overflow-hidden underline-offset-4 truncate text-xs text-muted-foreground leading-5",
                }}
                usernamePrefix="$"
              />

              <span className="text-xs text-muted">
                {row.original.tokenCategory}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "tradeValueUsd",
      header: ({ column }) =>
        renderSortingHeader({ column, title: "Value", className: "text-xxs" }),
      cell: (props) => {
        const { row, focusExchangeRate } = props as CustomCellContext;
        const valueUsd = row.original.tradeValueUsd || 0;
        return (
          <div className="flex items-center justify-start gap-1">
            <div
              className="
                flex flex-col gap-0 text-left font-mono text-xs text-foreground
              "
            >
              <CoinPriceFormatter
                price={valueUsd}
                highPriceThreshold={10}
                className={cn(
                  row.original.tradeType === TradeType.Buy
                    ? "text-emerald-400"
                    : "text-red-600",
                )}
              />
              <div
                className="
                  text-[10px] text-muted
                  hover:text-foreground
                "
              >
                <CoinPriceFormatter
                  price={usdToFocus(valueUsd, focusExchangeRate)}
                  highPriceThreshold={10}
                  isUsd={false}
                  className="text-[10px]"
                />{" "}
                <span>{FOCUS_TOKEN_DISPLAY_NAME}</span>
              </div>
            </div>
            <div>{renderTransactionVolumeBadgeBasedOnValue(valueUsd)}</div>
          </div>
        );
      },
    },
    {
      accessorKey: "tradePriceUsd",
      header: ({ column }) =>
        renderSortingHeader({ column, title: "Price", className: "text-xxs" }),
      cell: (props) => {
        const { row, focusExchangeRate } = props as CustomCellContext;
        const priceUsd = row.original.tradePriceUsd || 0;

        return (
          <div
            className="
            flex flex-col gap-0 text-left font-mono text-foreground
          "
          >
            <CoinPriceFormatter price={priceUsd || 0} className="text-xs" />
            <div
              className="
                flex items-center gap-1 text-[10px] text-muted
                hover:text-foreground
              "
            >
              <CoinPriceFormatter
                price={usdToFocus(priceUsd, focusExchangeRate)}
                highPriceThreshold={10}
                isUsd={false}
                className="text-[10px]"
              />{" "}
              <span>{FOCUS_TOKEN_DISPLAY_NAME}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "tokenMarketCapUsd",
      header: () => <span className="text-xxs">Market Cap</span>,
      cell: (props) => {
        const { row, focusExchangeRate } = props as CustomCellContext;
        const marketCapUsd = row.original.tokenMarketCapUsd || 0;
        return (
          <div
            className="
            flex flex-col gap-0 text-left font-mono text-foreground
          "
          >
            <CoinPriceFormatter price={marketCapUsd} className="text-xs" />
            <div
              className="
                flex items-center gap-1 text-[10px] text-muted
                hover:text-foreground
              "
            >
              <CoinPriceFormatter
                price={usdToFocus(marketCapUsd, focusExchangeRate)}
                highPriceThreshold={10}
                isUsd={false}
                className="text-[10px]"
              />{" "}
              <span>{FOCUS_TOKEN_DISPLAY_NAME}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "txnHashHex",
      header: () => <span className="text-xxs">Txn</span>,
      cell: (props) => {
        const { row } = props as CustomCellContext;
        if (!row.original.txnHashHex) return null;

        return (
          <TransactionCopyToClipboard
            transactionHash={row.original.txnHashHex}
            timestamp={row.original.tradeTimestamp}
            txnClassName="text-xs"
          />
        );
      },
    },
    {
      id: "actions",
      cell: (props) => {
        const { row, refetchData } = props as CustomCellContext;
        return (
          <TradeCoinActions
            coinUsername={row.original.tokenUsername ?? ""}
            coinPublicKey={row.original.tokenPublicKey ?? ""}
            hasAmm={row.original.tokenAmmCreatedAt !== null}
            traderUserName={row.original.traderUsername ?? ""}
            traderPublicKey={row.original.traderPublicKey ?? ""}
            refetchData={refetchData}
            hideWalletActions={true}
          />
        );
      },
    },
  ];
};
